import React, { useEffect, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import ClusteredMarkers from "@/components/ClusteredMarkers";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_ID || "halal_finder_map";

// Google Maps JS only reports auth errors (bad key, referrer mismatch,
// billing disabled) through the global `gm_authFailure` hook — nothing
// bubbles up as a JS error. Wire it once so we can render a helpful
// banner instead of the default silent gray tiles.
if (typeof window !== "undefined" && !window.__alk_gm_hooked) {
  window.__alk_gm_hooked = true;
  window.gm_authFailure = () => {
    window.__alk_gm_auth_failed = true;
    window.dispatchEvent(new Event("alk-gm-auth-failed"));
  };
}

// One-shot recenter triggered by `fitKey` (geolocate / city search / explicit
// recenter). We INTENTIONALLY pan-to-center at a fixed neighbourhood zoom
// instead of `fitBounds(restaurants)` — the latter pulled the camera to the
// centroid of all results which felt like "the map jumped to a different
// location" when the user's real GPS point was off-centre in the result set.
function FitOnKey({ fitKey, center }) {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map || !center) return;
    if (lastKey.current === fitKey) return;
    lastKey.current = fitKey;
    map.panTo(center);
    // Comfortable neighbourhood zoom; bounds-reporter still lets the user
    // pan/zoom freely afterwards.
    if (typeof map.getZoom === "function" && map.getZoom() < 14) {
      map.setZoom(14);
    }
  }, [map, fitKey, center]);
  return null;
}

// Pan (without zoom) to a selected active restaurant.
function PanToActive({ activeId, restaurants }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !activeId) return;
    const r = restaurants.find((x) => x.place_id === activeId);
    if (r) map.panTo({ lat: r.lat, lng: r.lng });
  }, [map, activeId, restaurants]);
  return null;
}

// Report (center, radius) when the map becomes idle after panning/zooming.
// Debounced 1200ms; only fires if center moved >25% of last radius OR zoom changed.
// `recenterKey` skips one idle cycle right after a programmatic recenter so the
// post-recenter idle doesn't get reported back as a user pan (which previously
// overwrote the user's GPS center with the map's settled position).
function BoundsReporter({ onBoundsChange, recenterKey }) {
  const map = useMap();
  const lastRef = useRef({ lat: null, lng: null, zoom: null });
  const timerRef = useRef(null);
  const skipNextRef = useRef(false);
  const seenKeyRef = useRef(recenterKey);

  // Arm the skip whenever the parent bumps recenterKey.
  useEffect(() => {
    if (recenterKey !== seenKeyRef.current) {
      seenKeyRef.current = recenterKey;
      skipNextRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [recenterKey]);

  useEffect(() => {
    if (!map || !onBoundsChange) return;

    const fire = () => {
      if (skipNextRef.current) {
        // Seed lastRef with the post-recenter position so the next real pan
        // is measured against the correct baseline.
        const c = map.getCenter();
        const z = map.getZoom();
        if (c && z != null) {
          lastRef.current = { lat: c.lat(), lng: c.lng(), zoom: z };
        }
        skipNextRef.current = false;
        return;
      }
      const bounds = map.getBounds();
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (!bounds || !center) return;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      // Half diagonal in meters using simple haversine
      const toRad = (d) => (d * Math.PI) / 180;
      const R = 6371000;
      const dLat = toRad(ne.lat() - sw.lat());
      const dLng = toRad(ne.lng() - sw.lng());
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(sw.lat())) *
          Math.cos(toRad(ne.lat())) *
          Math.sin(dLng / 2) ** 2;
      const diag = 2 * R * Math.asin(Math.sqrt(a));
      const radius = Math.min(50000, Math.max(500, Math.round(diag / 2)));

      // Movement gate
      const prev = lastRef.current;
      const lat = center.lat();
      const lng = center.lng();
      if (prev.lat != null && prev.zoom != null) {
        const moved =
          Math.hypot((lat - prev.lat) * 111000, (lng - prev.lng) * 85000) /
          Math.max(radius, 500);
        const zoomDiff = Math.abs(zoom - prev.zoom);
        if (moved < 0.25 && zoomDiff < 0.5) return;
      }
      lastRef.current = { lat, lng, zoom };
      onBoundsChange({ lat, lng, radius, zoom });
    };

    const handler = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fire, 1200);
    };

    const listener = map.addListener("idle", handler);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      window.google?.maps?.event?.removeListener(listener);
    };
  }, [map, onBoundsChange]);

  return null;
}

function UserMarker({ position }) {
  if (!position) return null;
  return (
    <AdvancedMarker position={position} zIndex={1}>
      <div className="relative" data-testid="user-location-marker">
        <span className="absolute -inset-3 rounded-full bg-[#3B82F6]/20 animate-ping" />
        <span className="relative block w-4 h-4 rounded-full bg-[#3B82F6] border-2 border-white shadow-md" />
      </div>
    </AdvancedMarker>
  );
}

export default function MapView({
  center,
  userLocation,
  restaurants,
  activeId,
  onMarkerClick,
  onBoundsChange,
  fitKey,
  initialZoom,
}) {
  const [authFailed, setAuthFailed] = React.useState(
    typeof window !== "undefined" && window.__alk_gm_auth_failed
  );

  React.useEffect(() => {
    const onFail = () => setAuthFailed(true);
    window.addEventListener("alk-gm-auth-failed", onFail);
    return () => window.removeEventListener("alk-gm-auth-failed", onFail);
  }, []);

  if (!API_KEY || authFailed) {
    return (
      <div
        data-testid="map-auth-failure"
        className="w-full h-full flex items-center justify-center bg-[#F2EDE3] p-6"
      >
        <div className="max-w-md text-center">
          <div className="text-4xl mb-3">🗺️</div>
          <h3 className="font-display text-lg text-[#2A3B2C] font-medium">
            {!API_KEY
              ? "Map isn't configured"
              : "Map couldn't authenticate"}
          </h3>
          <p className="text-sm text-[#647466] mt-2 leading-relaxed">
            {!API_KEY ? (
              "Set REACT_APP_GOOGLE_MAPS_API_KEY in the frontend env and redeploy."
            ) : (
              <>
                Google rejected the API key for this domain. Usually one of:
                <br />1. HTTP-referrer restriction missing the current domain
                <br />2. Billing not enabled on the Google Cloud project
                <br />3. Maps JavaScript API not enabled
              </>
            )}
          </p>
          <p className="text-[11px] text-[#8A9A8B] mt-3 font-mono">
            Open the browser console to see Google's exact error.
          </p>
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={API_KEY} libraries={["places"]}>
      <Map
        data-testid="halal-google-map"
        style={{ width: "100%", height: "100%" }}
        defaultCenter={center || { lat: -37.8136, lng: 144.9631 }}
        defaultZoom={initialZoom || 13}
        mapId={MAP_ID}
        gestureHandling="greedy"
        disableDefaultUI={true}
        zoomControl={true}
        clickableIcons={false}
      >
        <FitOnKey fitKey={fitKey} center={center} />
        <PanToActive activeId={activeId} restaurants={restaurants} />
        <BoundsReporter onBoundsChange={onBoundsChange} recenterKey={fitKey} />
        <UserMarker position={userLocation || null} />
        <ClusteredMarkers
          restaurants={restaurants}
          activeId={activeId}
          onMarkerClick={onMarkerClick}
        />
      </Map>
    </APIProvider>
  );
}
